import React, { useEffect, useState } from "react"
import { Tree, message, Spin, Input, Button, Typography, Space, Tag } from "antd"
import { getStorage, ref, listAll, getDownloadURL, getMetadata } from "firebase/storage"
import { FolderFilled, FileOutlined } from "@ant-design/icons"

const { Search } = Input
const { Text } = Typography

const VivaPlatesTree = () => {
  const [treeData, setTreeData] = useState([])
  const [loadingFolders, setLoadingFolders] = useState(true)
  const [searchValue, setSearchValue] = useState("")

  // Load top-level folders under vivaPlates/
  useEffect(() => {
    const storage = getStorage()
    const mainRef = ref(storage, "vivaPlates/")
    listAll(mainRef)
      .then((res) => {
        const folderNodes = res.prefixes.map((folderRef) => ({
          title: folderRef.name,
          key: folderRef.fullPath, // e.g., "vivaPlates/POLI123"
          isLeaf: false,
        }))
        setTreeData(folderNodes)
      })
      .catch((error) => {
        message.error("Error loading folders")
        console.error("Error loading folders: ", error)
      })
      .finally(() => {
        setLoadingFolders(false)
      })
  }, [])

  // Helper to update tree data immutably.
  const updateTreeData = (list, key, children) =>
    list.map((node) => {
      if (node.key === key) {
        return { ...node, children }
      }
      if (node.children) {
        return { ...node, children: updateTreeData(node.children, key, children) }
      }
      return node
    })

  // Helper to download a file given its path and name.
  const downloadFile = (fileKey, fileName) => {
    const storage = getStorage()
    const fileRef = ref(storage, fileKey)
    getDownloadURL(fileRef)
      .then((url) => {
        // Create an invisible link to force download
        const link = document.createElement("a")
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch((error) => {
        message.error("Error downloading file")
        console.error("Error downloading file: ", error)
      })
  }

  // Load files when a folder is expanded.
  const onLoadData = (treeNode) =>
    new Promise((resolve) => {
      if (treeNode.children) {
        resolve()
        return
      }
      const storage = getStorage()
      const folderRef = ref(storage, treeNode.key)
      listAll(folderRef)
        .then((res) => {
          // Filter out files with names ending in ".keep"
          const validItems = res.items.filter((fileRef) => !fileRef.name.endsWith(".keep"))
          // Fetch metadata for each file to get updated/created dates.
          Promise.all(
            validItems.map((fileRef) =>
              getMetadata(fileRef).then((metadata) => ({
                title: (
                  <Space>
                    <Text>{fileRef.name}</Text>
                    <Tag color="warning">{new Date(metadata.updated).toLocaleString("lv-LV")}</Tag>
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation()
                        downloadFile(fileRef.fullPath, fileRef.name)
                      }}>
                      Download
                    </Button>
                  </Space>
                ),
                key: fileRef.fullPath,
                isLeaf: true,
              }))
            )
          )
            .then((children) => {
              setTreeData((prev) => updateTreeData(prev, treeNode.key, children))
              resolve()
            })
            .catch((error) => {
              message.error("Error fetching file metadata")
              console.error("Error fetching metadata: ", error)
              resolve()
            })
        })
        .catch((error) => {
          message.error("Error loading files")
          console.error("Error loading files: ", error)
          resolve()
        })
    })

  // Optional: handle node selection if needed.
  const onSelect = (selectedKeys, info) => {
    // We rely on the explicit Download button for file nodes.
  }

  // Filter function for top-level folders based on the search input.
  const filterTreeData = (data, searchText) => {
    if (!searchText) return data
    return data.filter((node) =>
      String(node.title).toLowerCase().includes(searchText.toLowerCase())
    )
  }

  const filteredData = filterTreeData(treeData, searchValue)

  return (
    <div>
      <Search
        placeholder="Search folders"
        onChange={(e) => setSearchValue(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      {loadingFolders ? (
        <Spin tip="Loading folders..." />
      ) : (
        <Tree
          showIcon
          loadData={onLoadData}
          treeData={filteredData}
          onSelect={onSelect}
          icon={({ isLeaf }) => (isLeaf ? <FileOutlined /> : <FolderFilled />)}
        />
      )}
    </div>
  )
}

export default VivaPlatesTree
